import React, { useEffect, useState, useRef, Component } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { BookOpenIcon, CalculatorIcon, BeakerIcon, DnaIcon, PencilIcon, LandmarkIcon, GlobeIcon, PaletteIcon, MusicIcon, CodeIcon, ActivityIcon, FileTextIcon, TrophyIcon, ClockIcon, MoreVerticalIcon, ArchiveIcon, TrashIcon, UserIcon, SparklesIcon } from 'lucide-react';
import * as Tooltip from '@radix-ui/react-tooltip';
import { StatusBadge as AssessmentStatusBadge } from './AssessmentStatusBadge';
import type { StatusType } from './AssessmentStatusBadge';
interface AssignedReportCardProps {
  report: {
    id: number;
    title: string;
    mode: string;
    questions: number;
    dueDate?: string;
    dateAssigned?: string;
    dateStarted?: string;
    dateCompleted?: string;
    lastInteractedWith?: string;
    status: string;
    participants: number;
    icon: string;
    creator?: string;
    score?: number;
    assignedByTeacher?: boolean;
  };
  isArchived?: boolean;
  onArchiveToggle?: () => void;
}
// Icon mapping function based on title
const getIconForReport = (title: string, mode: string) => {
  const lowerTitle = title.toLowerCase();
  if (lowerTitle.includes('math') || lowerTitle.includes('algebra') || lowerTitle.includes('fraction')) {
    return CalculatorIcon;
  }
  if (lowerTitle.includes('science') || lowerTitle.includes('chemistry') || lowerTitle.includes('matter')) {
    return BeakerIcon;
  }
  if (lowerTitle.includes('biology') || lowerTitle.includes('body') || lowerTitle.includes('dna')) {
    return DnaIcon;
  }
  if (lowerTitle.includes('english') || lowerTitle.includes('grammar') || lowerTitle.includes('writing')) {
    return PencilIcon;
  }
  if (lowerTitle.includes('history') || lowerTitle.includes('civilization')) {
    return LandmarkIcon;
  }
  if (lowerTitle.includes('geography') || lowerTitle.includes('continent')) {
    return GlobeIcon;
  }
  if (lowerTitle.includes('art') || lowerTitle.includes('painting')) {
    return PaletteIcon;
  }
  if (lowerTitle.includes('music') || lowerTitle.includes('theory')) {
    return MusicIcon;
  }
  if (lowerTitle.includes('computer') || lowerTitle.includes('programming')) {
    return CodeIcon;
  }
  if (lowerTitle.includes('physical') || lowerTitle.includes('sport')) {
    return ActivityIcon;
  }
  return BookOpenIcon;
};
const getIconColor = (title: string, mode: string): string => {
  const lowerTitle = title.toLowerCase();
  if (lowerTitle.includes('math') || lowerTitle.includes('algebra')) return '#2563EB';
  if (lowerTitle.includes('science') || lowerTitle.includes('chemistry')) return '#00B75F';
  if (lowerTitle.includes('biology') || lowerTitle.includes('dna')) return '#8B5CF6';
  if (lowerTitle.includes('english') || lowerTitle.includes('grammar')) return '#FF9D33';
  if (lowerTitle.includes('history')) return '#FF6CA1';
  if (lowerTitle.includes('geography')) return '#2563EB';
  if (lowerTitle.includes('art')) return '#FF6CA1';
  if (lowerTitle.includes('music')) return '#8B5CF6';
  if (lowerTitle.includes('computer') || lowerTitle.includes('programming')) return '#2563EB';
  if (lowerTitle.includes('physical') || lowerTitle.includes('sport')) return '#00B75F';
  // Fallback to mode-based colors
  if (mode === 'Challenge') return '#FF9D33';
  if (mode === 'Coach') return '#2563EB';
  return '#00B75F';
};
// Derive tags from mode and title
const getTagsFromReport = (title: string, mode: string): string[] => {
  const tags: string[] = [];
  const lowerTitle = title.toLowerCase();
  // Subject tag
  if (lowerTitle.includes('math')) tags.push('Math');else if (lowerTitle.includes('science')) tags.push('Science');else if (lowerTitle.includes('english')) tags.push('English');else if (lowerTitle.includes('history')) tags.push('History');else if (lowerTitle.includes('geography')) tags.push('Geography');else if (lowerTitle.includes('art')) tags.push('Art');else if (lowerTitle.includes('music')) tags.push('Music');else if (lowerTitle.includes('computer')) tags.push('Computer Science');else if (lowerTitle.includes('physical')) tags.push('PE');else if (lowerTitle.includes('biology')) tags.push('Biology');
  // Mode tag
  tags.push(mode);
  return tags;
};
// Helper to map student/archive status → draft | ready | archive
const getAssessmentStatusType = (status: string, isArchived: boolean): StatusType => {
  if (isArchived) return 'archive';
  if (status === 'Completed') return 'ready';
  return 'draft';
};
const AssignedReportCard: React.FC<AssignedReportCardProps> = ({
  report,
  isArchived,
  onArchiveToggle
}) => {
  const [showMenu, setShowMenu] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const IconComponent = getIconForReport(report.title, report.mode);
  const iconColor = getIconColor(report.title, report.mode);
  const tags = getTagsFromReport(report.title, report.mode);
  // Calculate days until due date
  const getDaysUntilDue = () => {
    if (!report.dueDate) return null;
    const now = new Date();
    const due = new Date(report.dueDate);
    const diffTime = due.getTime() - now.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };
  const daysUntilDue = getDaysUntilDue();
  const showDueDateWarning = daysUntilDue !== null && daysUntilDue <= 7 && daysUntilDue >= 0;
  const getDueDateStyle = () => {
    if (daysUntilDue === null) return null;
    if (daysUntilDue <= 1) {
      return {
        bg: 'bg-red-500 bg-opacity-10',
        border: 'border-red-500 border-opacity-30',
        text: 'text-red-600',
        icon: 'text-red-500'
      };
    }
    if (daysUntilDue <= 3) {
      return {
        bg: 'bg-[#FF9D33] bg-opacity-10',
        border: 'border-[#FF9D33] border-opacity-30',
        text: 'text-[#FF9D33]',
        icon: 'text-[#FF9D33]'
      };
    }
    return {
      bg: 'bg-[#2563EB] bg-opacity-10',
      border: 'border-[#2563EB] border-opacity-30',
      text: 'text-[#2563EB]',
      icon: 'text-[#2563EB]'
    };
  };
  const dueDateStyle = getDueDateStyle();
  const getDueDateText = () => {
    if (daysUntilDue === null) return '';
    if (daysUntilDue === 0) return 'Due today';
    if (daysUntilDue === 1) return 'Due tomorrow';
    return `Due in ${daysUntilDue} days`;
  };
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setShowMenu(false);
      }
    };
    if (showMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showMenu]);
  const getActionButton = (status: string) => {
    switch (status) {
      case 'In progress':
        return {
          label: 'Resume',
          color: 'bg-[#FF9D33] hover:bg-[#ff8c1a]'
        };
      case 'Not started':
        return {
          label: 'Start',
          color: 'bg-[#00B75F] hover:bg-[#00a055]'
        };
      case 'Completed':
        return {
          label: 'View Report',
          color: 'bg-[#2563EB] hover:bg-[#1d4ed8]'
        };
      default:
        return {
          label: 'Start',
          color: 'bg-[#00B75F] hover:bg-[#00a055]'
        };
    }
  };
  const getTagColor = (index: number) => {
    const colors = ['bg-[#2563EB] bg-opacity-10 text-[#2563EB]', 'bg-[#00B75F] bg-opacity-10 text-[#00B75F]', 'bg-[#FF9D33] bg-opacity-10 text-[#FF9D33]', 'bg-[#FF6CA1] bg-opacity-10 text-[#FF6CA1]', 'bg-[#8B5CF6] bg-opacity-10 text-[#8B5CF6]'];
    return colors[index % colors.length];
  };
  const actionButton = getActionButton(report.status);
  const isCompleted = report.status === 'Completed';
  return <Tooltip.Provider delayDuration={200} data-id="element-534">
      <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.3
    }} whileHover={{
      y: -6
    }} className="h-[240px] bg-surface-elevated border-2 border-custom hover:border-[#00B75F] rounded-2xl hover:shadow-custom-lg transition-all duration-300 relative flex flex-col overflow-hidden group" data-id="element-535">
        {/* Menu Button - Top Left */}
        <div className="absolute top-3 left-3 z-10" ref={menuRef} data-id="element-536">
          <button className="flex items-center justify-center w-7 h-7 rounded-lg bg-surface hover:bg-[#00B75F] hover:bg-opacity-10 text-text-secondary hover:text-[#00B75F] transition-all duration-200 opacity-0 group-hover:opacity-100" aria-label="More actions" onClick={e => {
          e.stopPropagation();
          setShowMenu(!showMenu);
        }} data-id="element-537">
            <MoreVerticalIcon size={14} data-id="element-538" />
          </button>
          <AnimatePresence data-id="element-539">
            {showMenu && <motion.div initial={{
            opacity: 0,
            scale: 0.95,
            y: -10
          }} animate={{
            opacity: 1,
            scale: 1,
            y: 0
          }} exit={{
            opacity: 0,
            scale: 0.95,
            y: -10
          }} transition={{
            duration: 0.15
          }} className="absolute left-0 mt-2 w-44 bg-surface-elevated rounded-xl shadow-custom-lg border border-custom overflow-hidden z-[100]" onClick={e => e.stopPropagation()} data-id="element-540">
                <button className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-surface transition-colors text-left text-text-primary border-b border-custom" onClick={e => {
              e.stopPropagation();
              onArchiveToggle?.();
              setShowMenu(false);
            }} data-id="element-541">
                  <ArchiveIcon size={16} data-id="element-542" />
                  <span className="text-sm font-medium" data-id="element-543">
                    {isArchived ? 'Unarchive' : 'Archive'}
                  </span>
                </button>
                <button className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-surface transition-colors text-left text-red-600" onClick={e => {
              e.stopPropagation();
              console.log('Delete report:', report.id);
              setShowMenu(false);
            }} data-id="element-544">
                  <TrashIcon size={16} data-id="element-545" />
                  <span className="text-sm font-medium" data-id="element-546">Delete</span>
                </button>
              </motion.div>}
          </AnimatePresence>
        </div>

        {/* Action Button, Score, and Due Date - Top Right */}
        <div className="absolute top-3 right-3 z-10 flex flex-col items-end gap-2" data-id="element-547">
          <button className={`px-3 py-1.5 text-xs font-semibold text-white rounded-lg transition-colors ${actionButton.color}`} onClick={e => {
          e.stopPropagation();
          console.log('Action:', actionButton.label, report.id);
        }} data-id="element-548">
            {actionButton.label}
          </button>
          {isCompleted && report.score !== undefined && <Tooltip.Root data-id="element-549">
              <Tooltip.Trigger asChild data-id="element-550">
                <div className="flex items-center gap-1.5 px-2.5 py-1 bg-[#00B75F] bg-opacity-10 rounded-lg border border-[#00B75F] border-opacity-20 cursor-help" data-id="element-551">
                  <TrophyIcon size={12} className="text-[#00B75F]" data-id="element-552" />
                  <span className="text-xs font-bold text-[#00B75F]" data-id="element-553">
                    {report.score}%
                  </span>
                </div>
              </Tooltip.Trigger>
              <Tooltip.Portal data-id="element-554">
                <Tooltip.Content className="bg-surface-elevated border border-custom px-3 py-1.5 rounded-lg shadow-custom-lg text-xs font-medium text-text-primary z-50" sideOffset={5} data-id="element-555">
                  Your Score
                  <Tooltip.Arrow className="fill-surface-elevated" data-id="element-556" />
                </Tooltip.Content>
              </Tooltip.Portal>
            </Tooltip.Root>}
          {showDueDateWarning && dueDateStyle && <Tooltip.Root data-id="element-557">
              <Tooltip.Trigger asChild data-id="element-558">
                <motion.div initial={{
              scale: 0
            }} animate={{
              scale: 1
            }} className={`flex items-center gap-1.5 px-2.5 py-1 ${dueDateStyle.bg} rounded-lg border ${dueDateStyle.border} cursor-help`} data-id="element-559">
                  <ClockIcon size={12} className={dueDateStyle.icon} data-id="element-560" />
                  <span className={`text-xs font-bold ${dueDateStyle.text}`} data-id="element-561">
                    {getDueDateText()}
                  </span>
                </motion.div>
              </Tooltip.Trigger>
              <Tooltip.Portal data-id="element-562">
                <Tooltip.Content className="bg-surface-elevated border border-custom px-3 py-1.5 rounded-lg shadow-custom-lg text-xs font-medium text-text-primary z-50" sideOffset={5} data-id="element-563">
                  Due: {new Date(report.dueDate!).toLocaleDateString()}
                  <Tooltip.Arrow className="fill-surface-elevated" data-id="element-564" />
                </Tooltip.Content>
              </Tooltip.Portal>
            </Tooltip.Root>}
        </div>

        {/* Top Section - Icon and Title */}
        <div className="min-h-[140px] flex flex-col items-center justify-center px-4 pt-4 pb-3" data-id="element-565">
          <motion.div className="w-16 h-16 rounded-2xl flex items-center justify-center mb-3 group-hover:scale-110 transition-transform duration-300 relative" style={{
          backgroundColor: `${iconColor}15`
        }} data-id="element-566">
            <IconComponent size={32} style={{
            color: iconColor
          }} data-id="element-567" />
            {/* Assignment Type Badge on Icon */}
            {report.assignedByTeacher !== undefined && <Tooltip.Root data-id="element-568">
                <Tooltip.Trigger asChild data-id="element-569">
                  <div className="absolute -bottom-1 -right-1 w-5 h-5 bg-surface-elevated rounded-full flex items-center justify-center shadow-sm border border-custom" data-id="element-570">
                    {report.assignedByTeacher ? <UserIcon size={11} className="text-[#2563EB]" data-id="element-571" /> : <SparklesIcon size={11} className="text-[#FF9D33]" data-id="element-572" />}
                  </div>
                </Tooltip.Trigger>
                <Tooltip.Portal data-id="element-573">
                  <Tooltip.Content className="bg-surface-elevated border border-custom px-3 py-1.5 rounded-lg shadow-custom-lg text-xs font-medium text-text-primary z-50" sideOffset={5} data-id="element-574">
                    {report.assignedByTeacher ? 'Assigned' : 'Self-initiated'}
                    <Tooltip.Arrow className="fill-surface-elevated" data-id="element-575" />
                  </Tooltip.Content>
                </Tooltip.Portal>
              </Tooltip.Root>}
          </motion.div>
          <h3 className="font-bold text-text-primary text-center text-base leading-tight overflow-hidden text-ellipsis line-clamp-3 w-full px-8" title={report.title} data-id="element-576">
            {report.title}
          </h3>
        </div>

        {/* Bottom Section - Tags, Creator and Questions */}
        <div className="h-[100px] border-t-2 border-custom px-4 py-3 bg-surface bg-opacity-50 flex flex-col gap-2" data-id="element-577">
          <div className="flex items-center justify-between gap-1.5" data-id="element-578">
            <div className="flex items-center gap-1.5 flex-wrap flex-1" data-id="element-579">
              {tags.map((tag, index) => <span key={index} className={`inline-flex items-center text-xs font-semibold px-2 py-0.5 rounded-full ${getTagColor(index)}`} data-id="element-580">
                  {tag}
                </span>)}
            </div>
            <AssessmentStatusBadge status={getAssessmentStatusType(report.status, isArchived ?? false)} size="sm" data-id="element-581" />
          </div>
          <div className="flex items-center justify-between" data-id="element-582">
            <div className="flex flex-col items-start justify-center flex-1" data-id="element-583">
              <p className="text-xs text-text-tertiary mb-0.5" data-id="element-584">Created by</p>
              <p className="font-semibold text-text-primary text-sm overflow-hidden text-ellipsis whitespace-nowrap max-w-[120px]" title={report.creator || 'System'} data-id="element-585">
                {report.creator || 'System'}
              </p>
            </div>
            <Tooltip.Root data-id="element-586">
              <Tooltip.Trigger asChild data-id="element-587">
                <div className="flex items-center gap-2 cursor-help" data-id="element-588">
                  <FileTextIcon size={16} className="text-text-tertiary" data-id="element-589" />
                  <span className="text-lg font-bold text-text-primary" data-id="element-590">
                    {report.questions}
                  </span>
                </div>
              </Tooltip.Trigger>
              <Tooltip.Portal data-id="element-591">
                <Tooltip.Content className="bg-surface-elevated border border-custom px-3 py-1.5 rounded-lg shadow-custom-lg text-xs font-medium text-text-primary z-50" sideOffset={5} data-id="element-592">
                  Questions
                  <Tooltip.Arrow className="fill-surface-elevated" data-id="element-593" />
                </Tooltip.Content>
              </Tooltip.Portal>
            </Tooltip.Root>
          </div>
        </div>
      </motion.div>
    </Tooltip.Provider>;
};
export default AssignedReportCard;